import React, { useState, useMemo, useEffect } from 'react'
import { V4_T, formatCurrency, deriveProfitability } from './propertyDetailHelpers'

// Per-property profitability planning panel (the "Profitability" drawer tab).
// Interactive what-if: two sliders (price/visit, hours/visit) recompute margin
// live. Math lives in deriveProfitability(); this file is presentation + state.
// Spec: 2026-06-22-property-profitability-calculator (client implementation spec).
// v1 has no persistence — "save as prescribed" is a planned fast-follow.

const SERVICE_DOT = {
  irrigation: V4_T.green,
  herbicide: V4_T.plum,
  maintenance: V4_T.orange
}

const GAUGE_MAX = 60 // gauge scale: 0 → 60%

// Money with a unicode minus for cost lines.
const money = (value) => {
  const s = formatCurrency(Math.abs(value))
  return value < 0 ? `−${s}` : s
}

// ─── §8.1 Margin gauge ──────────────────────────────────────────────────────
function MarginGauge ({ margin, target, state }) {
  const pct = (v) => `${Math.max(0, Math.min(100, (v / GAUGE_MAX) * 100))}%`
  const fillColor = state === 'below' ? V4_T.amber : state === 'sparse' ? V4_T.mute : V4_T.green
  const hatch = 'repeating-linear-gradient(135deg, rgba(0,0,0,0.10) 0 6px, transparent 6px 11px)'
  const gapHatch = `repeating-linear-gradient(135deg, ${V4_T.rust}55 0 6px, transparent 6px 11px)`
  const currentFillEnd = Math.min(margin, target)

  return (
    <div style={{ margin: '10px 0 6px' }}>
      <div style={{ position: 'relative', height: 30, borderRadius: 7, background: V4_T.cream, border: `1px solid ${V4_T.lineSoft}`, overflow: 'hidden' }}>
        {/* current fill */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: pct(currentFillEnd), background: fillColor, backgroundImage: state === 'sparse' ? hatch : 'none', opacity: state === 'sparse' ? 0.7 : 1 }} />
        {/* overshoot (healthy, margin > target) */}
        {state === 'healthy' && margin > target && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(target), width: pct(margin - target), background: V4_T.greenDeep }} />
        )}
        {/* gap zone (below) */}
        {state === 'below' && margin < target && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(margin), width: pct(target - margin), backgroundImage: gapHatch }} />
        )}
        {/* target tick */}
        <div style={{ position: 'absolute', top: -2, bottom: -2, left: pct(target), width: 2, background: V4_T.ink }} />
      </div>
      <div style={{ position: 'relative', height: 14, marginTop: 3 }}>
        <span style={{ position: 'absolute', left: 0, fontSize: 9, fontWeight: 800, color: V4_T.muteSoft, fontFamily: V4_T.mono }}>0%</span>
        <span style={{ position: 'absolute', left: pct(target), transform: 'translateX(-50%)', fontSize: 9, fontWeight: 800, color: V4_T.mute, fontFamily: V4_T.mono, whiteSpace: 'nowrap' }}>TARGET {target}%</span>
      </div>
    </div>
  )
}

// ─── §8.2 Lever bar ─────────────────────────────────────────────────────────
function RateRow ({ row }) {
  const dot = SERVICE_DOT[row.key] || V4_T.mute
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 2px', borderTop: `1px solid ${V4_T.lineSoft}` }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0, marginRight: 9 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: V4_T.ink }}>
          {row.name}
          {row.estimated && (
            <span
              title="Man-hours estimated from clock-hours × crew (this property predates per-service man-hour tracking). Resync for the exact figure."
              style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: V4_T.amber, fontFamily: V4_T.mono, letterSpacing: '0.03em' }}
            >EST</span>
          )}
        </div>
        {row.perVisit != null && (
          <div style={{ fontSize: 10.5, color: V4_T.muteSoft, fontFamily: V4_T.mono }}>≈ {formatCurrency(row.perVisit)} / visit</div>
        )}
      </div>
      {row.rate == null ? (
        <span style={{ fontSize: 14, fontWeight: 800, color: V4_T.muteSoft, fontFamily: V4_T.mono }}>—</span>
      ) : (
        <span style={{ fontSize: 14, fontWeight: 800, color: V4_T.ink, fontFamily: V4_T.mono }}>
          {row.ok && <span style={{ color: V4_T.green, marginRight: 5 }}>✓</span>}
          {formatCurrency(row.rate)}<span style={{ fontSize: 10, color: V4_T.mute, fontWeight: 600 }}> / man-hr</span>
        </span>
      )}
    </div>
  )
}

function Card ({ classes, title, subtitle, children }) {
  return (
    <div className={classes.v4Card}>
      <div className={classes.v4CardHeader} style={{ marginBottom: 8 }}>
        <span className={classes.v4CardTitle}>{title}</span>
        {subtitle && <span className={classes.v4CardSubtitle}>{subtitle}</span>}
      </div>
      {children}
    </div>
  )
}

function BillingGroupBanner ({ billingGroup }) {
  if (!billingGroup) return null
  const memberNames = (billingGroup.members || [])
    .map(member => member.name || `Property #${member.propertyId}`)
    .join(', ')

  return (
    <div style={{ background: V4_T.greenSoft, border: `1px solid ${V4_T.green}55`, borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: V4_T.green, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 800, color: V4_T.green }}>Billing group: {billingGroup.name}</span>
      </div>
      <div style={{ fontSize: 11, color: V4_T.ink2, lineHeight: 1.45 }}>
        Profitability is calculated from summed revenue and costs for {memberNames || 'the included properties'}.
      </div>
    </div>
  )
}

// A labeled range slider with current value + a faint "baseline" tick mark.
function Slider ({ label, value, min, max, step, baseline, recommended, format, color, onChange }) {
  const pct = (v) => `${Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100))}%`
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 5 }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', color: V4_T.ink2, textTransform: 'uppercase' }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800, color, fontFamily: V4_T.mono }}>{format(value)}</span>
      </div>
      <div style={{ position: 'relative' }}>
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ width: '100%', accentColor: color, cursor: 'pointer', margin: 0 }}
        />
        {/* baseline + recommended ticks under the track */}
        <div style={{ position: 'relative', height: 12, marginTop: 1, fontSize: 8.5, fontFamily: V4_T.mono, color: V4_T.muteSoft }}>
          {baseline != null && (
            <span style={{ position: 'absolute', left: pct(baseline), transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>now {format(baseline)}</span>
          )}
          {recommended != null && Math.abs(recommended - (baseline ?? recommended)) > step && (
            <span style={{ position: 'absolute', left: pct(recommended), transform: 'translateX(-50%)', color: V4_T.green, whiteSpace: 'nowrap', top: baseline != null ? 0 : 0 }}>★</span>
          )}
        </div>
      </div>
    </div>
  )
}

// Hours-basis chip row. Active chip drives the slider + maintenance cost; chips
// with no data render disabled. Bases the calc reports as available are enabled.
const BASIS_META = [
  { key: 'actual', label: 'Actual', hint: 'avg of actual on-site hours' },
  { key: 'internal', label: 'Internal est', hint: 'trimmed estimate (high/low dropped)' },
  { key: 'prescribed', label: 'Prescribed', hint: 'manager-set hours' }
]
function BasisChips ({ basisHours, availableBases, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
      {BASIS_META.map(({ key, label, hint }) => {
        const value = basisHours?.[key]
        const enabled = availableBases?.includes(key)
        const isActive = active === key
        return (
          <button
            key={key} type="button" disabled={!enabled} title={enabled ? hint : 'No data for this basis'}
            onClick={() => enabled && onChange(key)}
            style={{
              padding: '5px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: enabled ? 'pointer' : 'default',
              fontFamily: V4_T.mono,
              border: `1px solid ${isActive ? V4_T.green : V4_T.line}`,
              background: isActive ? V4_T.greenSoft : (enabled ? '#fff' : V4_T.surface2),
              color: isActive ? V4_T.green : (enabled ? V4_T.ink2 : V4_T.muteSoft)
            }}
          >
            {label}{enabled && value != null ? ` · ${Number(value).toFixed(1)}h` : ''}
          </button>
        )
      })}
    </div>
  )
}

export default function ProfitabilityPanel ({ classes, sidebarData, profitSettings }) {
  const billingGroup = sidebarData.billingGroup || null

  // Which hours basis drives the slider + maintenance cost: actual (default) /
  // internal est (trimmed) / prescribed. Changing it re-seeds the sliders.
  const [hoursBasis, setHoursBasis] = useState('actual')

  // Baseline (un-overridden) derivation seeds the sliders + ranges.
  // profitSettings = global defaults (target/burdenRate/mileageRate/driveRoundTripFactor),
  // spread FIRST so the panel-local keys (hoursBasis/driveOverride/overrides) win on
  // collision; a null profitSettings spreads nothing → the calc uses its constants.
  const base = useMemo(() => deriveProfitability(sidebarData, { ...(profitSettings || {}), hoursBasis, driveOverride: sidebarData.driveOverride || null }), [sidebarData, hoursBasis, profitSettings])
  const { baseline, recommended, availableBases, basisHours, crewSizeAlert } = base

  // Interactive slider state. null until the user (or "snap") sets values.
  const [price, setPrice] = useState(baseline.pricePerVisit)
  const [hours, setHours] = useState(baseline.hoursPerVisit)
  const [touched, setTouched] = useState(false)

  // Re-seed when the property OR the active basis changes.
  useEffect(() => {
    setPrice(baseline.pricePerVisit)
    setHours(baseline.hoursPerVisit)
    setTouched(false)
  }, [baseline.pricePerVisit, baseline.hoursPerVisit])

  // Live result reflects the slider overrides once touched.
  const p = useMemo(
    () => touched ? deriveProfitability(sidebarData, { ...(profitSettings || {}), hoursBasis, driveOverride: sidebarData.driveOverride || null, overrides: { pricePerVisit: price, hoursPerVisit: hours } }) : base,
    [sidebarData, base, touched, price, hours, hoursBasis, profitSettings]
  )
  const { state, margin, target, gapPts, pnl, rates, completed } = p

  // Slider ranges (spec): price 0.5×–2× current, hours 0.3×–1.5× current.
  const priceMin = Math.max(0, Math.round(baseline.pricePerVisit * 0.5 / 5) * 5)
  const priceMax = Math.max(priceMin + 5, Math.round(baseline.pricePerVisit * 2 / 5) * 5)
  const hoursMin = Math.max(0.1, Math.round(baseline.hoursPerVisit * 0.3 * 10) / 10)
  const hoursMax = Math.max(hoursMin + 0.1, Math.round(baseline.hoursPerVisit * 1.5 * 10) / 10)
  const canInteract = state !== 'sparse' && baseline.pricePerVisit > 0 && baseline.hoursPerVisit > 0

  const setPriceLive = (v) => { setPrice(v); setTouched(true) }
  const setHoursLive = (v) => { setHours(v); setTouched(true) }
  const snapToRecommended = () => {
    if (!recommended) return
    setPrice(recommended.pricePerVisit)
    setHours(recommended.hoursPerVisit)
    setTouched(true)
  }
  const reset = () => {
    setPrice(baseline.pricePerVisit)
    setHours(baseline.hoursPerVisit)
    setTouched(false)
  }

  // Scenario-drawer guard: when opened from the Refine planner the property
  // starts as a thin stub (no completed/annual/cost history) and is hydrated
  // with the live routing property a tick later. Presenting the stub's zeros as
  // a real "0 completed · 100% margin" is wrong — wait for hydration instead.
  const awaitingHydration = !!(sidebarData.property && sidebarData.property._awaitingHydration)
  if (awaitingHydration) {
    return (
      <Card classes={classes} title="Where we are" subtitle="loading live data…">
        <div style={{ padding: '18px 4px', fontSize: 12, color: V4_T.ink2, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⏳</span>
          Loading this property's revenue and cost history…
        </div>
      </Card>
    )
  }

  const marginColor = margin >= target ? V4_T.green : state === 'sparse' ? V4_T.mute : V4_T.amber
  const deltaChip = state === 'sparse'
    ? null
    : margin >= target
      ? { text: `+${gapPts} pts over`, color: V4_T.green }
      : { text: `${gapPts} pts under`, color: V4_T.rust }

  return (
    <>
      <BillingGroupBanner billingGroup={billingGroup} />
      {/* ── Card 1: Where we are ── */}
      <Card classes={classes} title="Where we are" subtitle={billingGroup ? 'annual · billing group' : 'annual · full property'}>
        {state === 'sparse' && (
          <div style={{ background: V4_T.rustSoft, border: `1px solid ${V4_T.amber}55`, borderRadius: 7, padding: '7px 10px', marginBottom: 10, fontSize: 11, color: V4_T.ink2 }}>
            <b style={{ color: V4_T.amber }}>Limited history</b> — only {completed} completed visits. Figures are rough estimates.
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 2 }}>
          <span style={{ fontSize: 34, fontWeight: 800, color: marginColor, fontFamily: V4_T.font, lineHeight: 1 }}>
            {state === 'sparse' ? '~' : ''}{margin}%
          </span>
          <span title="Full-year projection — costs scaled from completed visits to the annual contract. The Financials tab and the ribbon Margin show real year-to-date margin." style={{ fontSize: 11, color: V4_T.mute, marginBottom: 4 }}>projected margin</span>
          {deltaChip && (
            <span style={{ marginLeft: 'auto', marginBottom: 4, fontSize: 11, fontWeight: 800, color: deltaChip.color, fontFamily: V4_T.mono }}>{deltaChip.text}</span>
          )}
        </div>

        <MarginGauge margin={margin} target={target} state={state} />

        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 2px' }}>
            <span style={{ flex: 1, fontSize: 12, color: V4_T.ink2 }}>Contracted revenue</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink, fontFamily: V4_T.mono }}>{formatCurrency(pnl.revenue)}</span>
          </div>
          {pnl.lines.map(([label, cost, meta]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', padding: '8px 2px', borderTop: `1px solid ${V4_T.lineSoft}` }}>
              <span style={{ flex: 1, fontSize: 12, color: V4_T.ink2 }}>
                {label}
                {meta?.assumed && (
                  <span title="Assumed minimum for a contracted service — projected, not actual history" style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 700, color: V4_T.amber, fontFamily: V4_T.mono, letterSpacing: '0.03em' }}>ASSUMED</span>
                )}
                {meta?.drive && p.drive && (
                  <div
                    title="Modeled one-way drive, yard→property (OSRM). Labor = drive hours × crew × rate; mileage = miles × $/mile."
                    style={{ fontSize: 10.5, color: V4_T.muteSoft, fontFamily: V4_T.mono }}
                  >
                    {p.drive.hoursPerVisit.toFixed(2)}h × {p.drive.crew} crew · {p.drive.milesPerVisit.toFixed(1)} mi
                    {p.drive.source && p.drive.source !== 'yard' ? ` · ${String(p.drive.source).replace(/_/g, ' ')}` : ' · yard est'}
                    {p.drive.source !== 'yard' && p.drive.referenceYardHours != null
                      ? ` (vs ${p.drive.referenceYardHours.toFixed(2)}h yard ref)`
                      : ''}
                  </div>
                )}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: meta?.assumed ? V4_T.amber : V4_T.mute, fontFamily: V4_T.mono }}>{money(cost)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', padding: '9px 10px', marginLeft: -8, marginRight: -8, marginTop: 2, background: V4_T.surface2, borderTop: `1px solid ${V4_T.lineSoft}` }}>
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: V4_T.ink }}>Annual profit</span>
            <span style={{ fontSize: 10.5, color: V4_T.muteSoft, marginRight: 10, fontFamily: V4_T.mono }}>{pnl.marginPct}% margin</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: state === 'healthy' ? V4_T.green : V4_T.ink, fontFamily: V4_T.mono }}>{formatCurrency(pnl.profit)}</span>
          </div>
          <div style={{ fontSize: 9.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 8, letterSpacing: '0.02em' }}>
            FULL-YEAR: COSTS PROJECTED FROM {completed} COMPLETED TO {baseline.annualVisits || '—'} ANNUAL VISITS.
          </div>
          <div style={{ fontSize: 9.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 4, letterSpacing: '0.02em' }}>
            ALL SERVICES, FULL-YEAR — FINANCIALS TAB SHOWS ALL SERVICES BY PERIOD.
          </div>
          {pnl.usesAssumedMinimums && (
            <div style={{ fontSize: 9.5, color: V4_T.amber, fontFamily: V4_T.mono, marginTop: 4, letterSpacing: '0.02em' }}>
              INCLUDES ASSUMED MINIMUMS FOR CONTRACTED SERVICES — PROJECTED, NOT ACTUAL HISTORY.
            </div>
          )}
        </div>
      </Card>

      {/* ── Card 2: Play with it (interactive what-if) ── */}
      <Card classes={classes} title="Play with it" subtitle={touched ? 'what-if · unsaved' : 'drag to model a renewal'}>
        {!canInteract ? (
          <div style={{ fontSize: 12, color: V4_T.mute, padding: '6px 2px' }}>
            {state === 'sparse'
              ? `Not enough completed visits (${completed} on file) to model a renewal. Check back next quarter.`
              : 'No contract price or hours on file yet — nothing to model.'}
          </div>
        ) : (
          <>
            {/* live margin transition: baseline → current slider margin */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: V4_T.cream, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: V4_T.mute }}>now</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: V4_T.mute, fontFamily: V4_T.mono }}>{base.margin}%</span>
              <span style={{ color: V4_T.mute }}>→</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: marginColor, fontFamily: V4_T.mono }}>{margin}%</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: margin >= target ? V4_T.green : V4_T.rust, fontFamily: V4_T.mono }}>
                {margin >= target ? '✓ at target' : `${gapPts} pts under`}
              </span>
            </div>

            {/* Efficiency-first guidance — why the ★ marks sit where they do. */}
            {recommended && (
              <div style={{ fontSize: 11, color: V4_T.ink2, background: V4_T.surface2, border: `1px solid ${V4_T.lineSoft}`, borderRadius: 7, padding: '7px 10px', marginBottom: 12, lineHeight: 1.5 }}>
                {base.margin >= base.target ? (
                  <><b style={{ color: V4_T.green }}>Hold.</b> Above target — keep price and hours as-is.</>
                ) : recommended.priceChanged ? (
                  <><b style={{ color: V4_T.amber }}>Trim hours first</b> to ★ {recommended.hoursPerVisit.toFixed(1)}h, then raise price to ★ {formatCurrency(recommended.pricePerVisit)} for the rest.</>
                ) : (
                  <><b style={{ color: V4_T.green }}>Efficiency alone gets there.</b> Trim hours to ★ {recommended.hoursPerVisit.toFixed(1)}h — no price increase needed.</>
                )}
              </div>
            )}

            <Slider
              label="Price / visit" color={V4_T.green}
              value={price} min={priceMin} max={priceMax} step={5}
              baseline={baseline.pricePerVisit} recommended={recommended?.pricePerVisit}
              format={(v) => formatCurrency(v)} onChange={setPriceLive}
            />
            {/* Hours-basis selector — which on-site-hours basis the model uses. */}
            <div style={{ fontSize: 10, fontWeight: 700, color: V4_T.muteSoft, fontFamily: V4_T.mono, letterSpacing: '0.04em', marginBottom: 6 }}>HOURS BASIS</div>
            <BasisChips basisHours={basisHours} availableBases={availableBases} active={base.hoursBasis} onChange={setHoursBasis} />
            {crewSizeAlert && (
              <div style={{ fontSize: 10.5, color: V4_T.amber, fontFamily: V4_T.mono, marginBottom: 8, lineHeight: 1.4 }}>
                ⚠ CREW SIZE {crewSizeAlert} — INTERNAL / PRESCRIBED BASES ASSUME A SMALLER CREW; FIGURES MAY SKEW.
              </div>
            )}
            <Slider
              label={`On-site hours / visit · ${base.hoursBasis}`} color={V4_T.amber}
              value={hours} min={hoursMin} max={hoursMax} step={0.1}
              baseline={baseline.hoursPerVisit} recommended={recommended?.hoursPerVisit}
              format={(v) => `${Number(v).toFixed(1)}h`} onChange={setHoursLive}
            />

            {/* live annual profit echo */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: V4_T.surface2, borderRadius: 8, padding: '8px 12px', margin: '4px 0 10px' }}>
              <span style={{ fontSize: 11.5, color: V4_T.ink2 }}>Annual profit at these settings</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: pnl.profit >= 0 ? V4_T.green : V4_T.rust, fontFamily: V4_T.mono }}>{formatCurrency(pnl.profit)}</span>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              {recommended && (recommended.priceChanged || recommended.hoursChanged) && (
                <button
                  type="button" onClick={snapToRecommended}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 7, border: `1px solid ${V4_T.green}`, background: V4_T.greenSoft, color: V4_T.green, fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}
                  title={recommended.priceChanged
                    ? 'Trim hours toward the floor, then raise price for the remainder'
                    : 'Trim hours toward the floor — no price increase needed'}
                >
                  ★ Snap to recommended
                  {recommended.priceChanged ? ' (hours + price)' : ' (hours only)'}
                </button>
              )}
              <button
                type="button" onClick={reset} disabled={!touched}
                style={{ padding: '8px 12px', borderRadius: 7, border: `1px solid ${V4_T.line}`, background: '#fff', color: touched ? V4_T.ink2 : V4_T.muteSoft, fontSize: 11.5, fontWeight: 700, cursor: touched ? 'pointer' : 'default' }}
              >
                Reset
              </button>
            </div>

            <div style={{ fontSize: 9.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 9, letterSpacing: '0.02em' }}>
              IRRIGATION / HERBICIDE / DRIVE / MATERIAL COSTS HELD FIXED. NOT SAVED.
            </div>
          </>
        )}
      </Card>

      {/* ── Card 3: What we should charge ── */}
      <Card classes={classes} title="What we should charge" subtitle={state === 'healthy' ? 'current rates clear target' : `to hold ${target}% margin`}>
        {rates.map(row => <RateRow key={row.key} row={row} />)}
        <div style={{ fontSize: 9.5, color: V4_T.muteSoft, fontFamily: V4_T.mono, marginTop: 8, letterSpacing: '0.02em' }}>
          {state === 'sparse'
            ? 'RATES UNLOCK ONCE THIS PROPERTY HAS ENOUGH VISIT HISTORY.'
            : `RATE = LOADED LABOR + MATERIALS HELD TO ${target}% OF PRICE.`}
        </div>
      </Card>
    </>
  )
}
